import React from 'react';
import PropTypes from 'prop-types';

import DotsIcon from 'mdi-react/DotsVerticalIcon';
import Rating from '@prisjakt/ui-rating';
import T from '@prisjakt/ui-translate';
import Dropdown from '@prisjakt/ui-dropdown';

import { AppContext } from '@prisjakt/ui-app-context';
import * as S from './styles';

class UserReviewRow extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showMore: false,
      isExpanded: false,
      showOriginal: false,
    };
  }

  componentDidMount() {
    if (!this.textNode) return;

    const textHeight = this.textNode.getBoundingClientRect().height;
    const wrapperHeight = this.textNode.parentElement.getBoundingClientRect()
      .height;

    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      showMore: textHeight > wrapperHeight,
    });
  }

  toggleExpansion() {
    this.setState({
      isExpanded: !this.state.isExpanded,
    });
  }

  toggleTranslation() {
    this.setState({
      showOriginal: !this.state.showOriginal,
    });
  }

  renderText() {
    const t = this.context.t;
    const collapseOrExpand = this.state.isExpanded
      ? 'symbol.collapse'
      : 'symbol.expand';
    const reviewTextIdentifier = `review-text-${this.props.id}`;
    const expansionText = this.state.isExpanded
      ? t('common', 'button.show-less')
      : t('common', 'button.show-more');

    const text = this.state.showOriginal
      ? this.props.translation.text
      : this.props.text;

    return (
      <div>
        <S.TextLabel id={reviewTextIdentifier} fullText={this.state.isExpanded}>
          <div
            ref={node => {
              this.textNode = node;
            }}
          >
            {text && text.trim()}
          </div>
        </S.TextLabel>

        {this.state.showMore && (
          <S.ReadMore
            onClick={() => this.toggleExpansion()}
            aria-label={t('symbols', collapseOrExpand)}
            aria-expanded={this.state.isExpanded}
            aria-controls={reviewTextIdentifier}
          >
            {expansionText}
          </S.ReadMore>
        )}
        {this.props.translation && this.renderTranslation()}
        {this.props.reply && this.renderReply()}
      </div>
    );
  }

  renderReply() {
    const { name, text, date } = this.props.reply;
    if (!text) {
      return null;
    }

    return (
      <S.Reply>
        <S.ReplyName>
          {this.context.t('reviews', 'store.reply-by', {
            name,
          })}
          {date && <S.ReplyDate>{date}</S.ReplyDate>}
        </S.ReplyName>
        <S.ReplyText>{text.trim()}</S.ReplyText>
      </S.Reply>
    );
  }

  renderTranslation() {
    if (!this.props.translation.text) {
      return null;
    }

    return (
      <S.TranslationOptions>
        <S.TranslationOptionsService>
          <T domain="reviews">
            {this.props.translation.service
              ? `translation.translated-by-${this.props.translation.service.name.toLowerCase()}.md`
              : 'translation.translated'}
          </T>
        </S.TranslationOptionsService>
        <S.TranslationOptionsToggle>
          <button
            type="button"
            onClick={() => {
              this.toggleTranslation();
            }}
          >
            {this.state.showOriginal
              ? this.context.t('reviews', 'translation.show-translation')
              : this.context.t('reviews', 'translation.show-original')}
          </button>
        </S.TranslationOptionsToggle>
      </S.TranslationOptions>
    );
  }

  renderActions() {
    const { actions, id } = this.props;
    return !actions || !actions.length ? null : (
      <S.Actions>
        <Dropdown
          title={<DotsIcon />}
          type="select-box-independent-type"
          headerPosition="right"
          offsetOrigin="right"
          options={actions.map((item, index) => ({
            name: item.name,
            value: `${index}`,
          }))}
          onSelect={option => {
            if (typeof actions[option.value].handler === 'function') {
              actions[option.value].handler({ id });
            }
          }}
        />
      </S.Actions>
    );
  }

  renderUser() {
    return (
      <S.User>
        <S.UserAvatar>{this.props.avatar}</S.UserAvatar>
        <S.UserInfo>
          <S.UsernameLabel>{this.props.displayName}</S.UsernameLabel>
          {this.props.userReviewCount > 0 && (
            <S.ReviewCountLabel>
              <T domain="reviews" n={this.props.userReviewCount}>
                user.total-reviews
              </T>
            </S.ReviewCountLabel>
          )}
        </S.UserInfo>
      </S.User>
    );
  }

  renderEditControl() {
    const { editControl } = this.props;

    return editControl ? <S.Edit>{editControl}</S.Edit> : null;
  }

  render() {
    const {
      rating,
      styling,
      date,
      editDate,
      purchaseDate,
      className,
    } = this.props;

    return (
      <S.UserReviewRow className={`row ${className || ''}`}>
        <div className="column small-12 large-2">
          <S.Rating>
            <Rating
              rate={rating}
              showCounter={false}
              secondaryColors={styling === 'archived'}
            />
            <S.DateLabel createdDate>{date}</S.DateLabel>
            <div>
              {editDate && (
                <S.DateLabel editDate>
                  <T domain="reviews" date={editDate}>
                    info.last-edited
                  </T>
                </S.DateLabel>
              )}
              {purchaseDate && (
                <S.DateLabel purchaseDate>
                  <T domain="reviews" date={purchaseDate}>
                    info.purchased
                  </T>
                </S.DateLabel>
              )}
            </div>
          </S.Rating>
        </div>
        <S.MainBlock className="column">
          {this.renderEditControl()}
          {this.renderActions()}
          {this.renderUser()}
          {this.renderText()}
        </S.MainBlock>
      </S.UserReviewRow>
    );
  }
}

const { string, number, func, oneOf, arrayOf, shape, element } = PropTypes;

UserReviewRow.contextType = AppContext;

UserReviewRow.propTypes = {
  id: string.isRequired,
  rating: number,
  date: string,
  editDate: string,
  purchaseDate: string,

  avatar: element.isRequired,
  displayName: string,
  userReviewCount: number,

  text: string,
  translation: shape({
    text: string.isRequired,
    language: string,
    service: shape({
      name: string.isRequired,
      uri: string,
    }),
  }),

  reply: shape({
    name: string.isRequired,
    text: string.isRequired,
    date: string,
  }),

  styling: oneOf(['default', 'archived']),
  editControl: element,
  actions: arrayOf(
    shape({
      name: string,
      handler: func,
    })
  ),
  className: string,
};

UserReviewRow.defaultProps = {
  rating: 0,
  date: '',
  editDate: '',
  purchaseDate: '',
  text: '',
  translation: undefined,
  reply: undefined,

  displayName: '',
  userReviewCount: 0,

  styling: 'default',
  editControl: null,
  actions: [],
  className: undefined,
};

export default UserReviewRow;
